import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { WarehousingItem, WarehousingStockLot } from '../warehousing.model';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingService } from '../warehousing.service';
import { itemIdentityLabel } from '../warehousing-item-display';

@Component({
  selector: 'app-warehousing-quality-lots',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule, ButtonModule, InputTextModule, TableModule,
    TagModule, ToastModule, WarehousingPageHeaderComponent, AuditDirectoryCellComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    <app-warehousing-page-header
      title="Quality Lots"
      subtitle="Release or reject traceable receipt lots after cold-chain and seafood quality checks."
      activeSection="quality">
      <div actions class="flex gap-2">
        <input pInputText [(ngModel)]="qualityNotes" placeholder="Quality action notes" />
        <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true"
          [loading]="loading" (onClick)="load()"></p-button>
      </div>
    </app-warehousing-page-header>

    <p-table [value]="lots" [loading]="loading" [paginator]="true" [rows]="20"
      [rowsPerPageOptions]="[20, 50, 100]" styleClass="p-datatable-sm"
      [tableStyle]="{ 'min-width': '78rem' }">
      <ng-template pTemplate="header">
        <tr>
          <th>Lot</th><th>Item</th><th>Origin</th><th>Expiry</th><th>Receipt temp.</th>
          <th>Quality</th><th>Source</th><th>Container</th><th>Audit</th><th class="text-right">Actions</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-lot>
        <tr>
          <td><strong>{{ lot.lotNumber || ('#' + lot.id) }}</strong><br><small>{{ lot.supplierLotNumber || '-' }}</small></td>
          <td>{{ itemName(lot.itemId) }}</td>
          <td>{{ lot.originReference || (lot.originCountryId ? ('Country #' + lot.originCountryId) : '-') }}</td>
          <td>{{ lot.expiryDate | date:'mediumDate' }}</td>
          <td>{{ lot.receiptTemperatureC ?? '-' }}{{ lot.receiptTemperatureC != null ? ' °C' : '' }}<br>
            <p-tag *ngIf="lot.temperatureStatus" [value]="lot.temperatureStatus" [severity]="temperatureSeverity(lot.temperatureStatus)"></p-tag>
          </td>
          <td><p-tag [value]="lot.qualityStatus" [severity]="qualitySeverity(lot.qualityStatus)"></p-tag><br>
            <small>{{ lot.qualityNotes || '-' }}</small>
          </td>
          <td>
            <a *ngIf="lot.purchaseOrderId" [routerLink]="['/order/purchase-order/view', lot.purchaseOrderId]">PO #{{ lot.purchaseOrderId }}</a>
            <span *ngIf="!lot.purchaseOrderId">-</span>
            <br>
            <a *ngIf="lot.purchaseOrderId && lot.purchaseOrderReceiptId"
              [routerLink]="['/order/purchase-order/view', lot.purchaseOrderId, 'receipts', lot.purchaseOrderReceiptId]">
              GRN #{{ lot.purchaseOrderReceiptId }}
            </a>
          </td>
          <td>
            <a *ngIf="lot.containerCaseId" [routerLink]="['/container-tracking/cases/view', lot.containerCaseId]">Case #{{ lot.containerCaseId }}</a>
            <span *ngIf="!lot.containerCaseId">-</span>
          </td>
          <td><app-audit-directory-cell [stamp]="lot"></app-audit-directory-cell></td>
          <td class="text-right">
            <div class="flex justify-content-end gap-2" *ngIf="lot.qualityStatus === 'HOLD'">
              <p-button label="Release" icon="pi pi-check" size="small" severity="success"
                [disabled]="!canRelease() || actingLotId === lot.id" [loading]="actingLotId === lot.id"
                (onClick)="applyAction(lot, 'RELEASE')"></p-button>
              <p-button label="Reject" icon="pi pi-times" size="small" severity="danger" [outlined]="true"
                [disabled]="!canReject() || !!actingLotId"
                [loading]="actingLotId === lot.id"
                (onClick)="applyAction(lot, 'REJECT')"></p-button>
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="10" class="text-center p-5">No receipt lots found.</td></tr></ng-template>
    </p-table>
  `
})
export class WarehousingQualityLotsComponent implements OnInit {
  private warehousingService = inject(WarehousingService);
  private permissionService = inject(PermissionService);
  private messageService = inject(MessageService);
  private route = inject(ActivatedRoute);

  lots: WarehousingStockLot[] = [];
  items: WarehousingItem[] = [];
  qualityNotes = '';
  loading = false;
  actingLotId?: number;

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    forkJoin({ lots: this.warehousingService.getStockLots(), items: this.warehousingService.getItems() })
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: response => {
          const receiptId = Number(this.route.snapshot.queryParamMap.get('purchaseOrderReceiptId')) || undefined;
          this.lots = (response.lots.data || []).filter(lot => !receiptId || lot.purchaseOrderReceiptId === receiptId);
          this.items = response.items.data || [];
        },
        error: error => this.showError(error, 'Unable to load quality lots')
      });
  }

  applyAction(lot: WarehousingStockLot, action: 'RELEASE' | 'REJECT'): void {
    if (this.actingLotId) return;
    this.actingLotId = lot.id;
    const request = action === 'RELEASE'
      ? this.warehousingService.releaseStockLot(lot.id, this.qualityNotes)
      : this.warehousingService.rejectStockLot(lot.id, this.qualityNotes);
    request.pipe(finalize(() => this.actingLotId = undefined)).subscribe({
      next: response => {
        const index = this.lots.findIndex(row => row.id === lot.id);
        if (index >= 0) this.lots = this.lots.map((row, i) => i === index ? response.data : row);
        this.qualityNotes = '';
        this.messageService.add({ severity: 'success', summary: `Lot ${action === 'RELEASE' ? 'released' : 'rejected'}` });
      },
      error: error => this.showError(error, `Unable to ${action.toLowerCase()} lot`)
    });
  }

  itemName(itemId: number): string {
    const item = this.items.find(row => row.id === itemId);
    return itemIdentityLabel(item, `Item #${itemId}`);
  }

  canRelease(): boolean {
    return this.permissionService.hasPermission(app_permissions.warehousing.quality.release);
  }

  canReject(): boolean {
    return this.permissionService.hasPermission(app_permissions.warehousing.quality.reject);
  }

  qualitySeverity(status?: string): 'success' | 'warn' | 'danger' | 'secondary' {
    if (status === 'RELEASED') return 'success';
    if (status === 'REJECTED') return 'danger';
    return status === 'HOLD' ? 'warn' : 'secondary';
  }

  temperatureSeverity(status?: string): 'success' | 'danger' | 'secondary' {
    return ['PASS', 'IN_RANGE'].includes(status || '') ? 'success'
      : ['FAIL', 'OUT_OF_RANGE'].includes(status || '') ? 'danger' : 'secondary';
  }

  private showError(error: any, fallback: string): void {
    this.messageService.add({ severity: 'error', summary: error?.error?.message || fallback });
  }
}
